/**
 * Mappings → Network access (Phase 7; ADR 0158): which of the office
 * SonicWall's connections count. A connection counts only from a tech's office
 * address, to a client's address, on one of these ports (decision 6). Each
 * port has a service, SSH to Other (the owner, 2026-10-16; ADR 0165): a tech's
 * connections to one client for one service are one session, and one
 * activity, while no gap between them is longer than the gap (decision 7). A
 * connection from an excluded source, the Pritunl server's, whose VPN traffic
 * hides who it's from, counts for no one (decision 3).
 *
 * Each change saves as it's made, as the other settings do (ADR 0082): the gap
 * when its field is left or Enter is pressed, a port or source as it's added
 * or removed, a port's service as it's chosen. One save at a time, and a
 * change made meanwhile is sent once that's answered. A value that can't be
 * saved is explained under its field and isn't sent; a refused one goes back
 * to what's saved, with the reason. The API checks every value again (an
 * address or range above all), keeps each port's service in step with the
 * ports, and audits each change.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useId, useRef, useState } from "react";

import { ApiError, retryUnlessRefused } from "../../api/client";
import type { AppSettings, SettingsChanges } from "../../api/types";
import { useSession } from "../../app/session-context";
import { useOneSaveAtATime } from "../../app/useOneSaveAtATime";
import { Panel } from "../../components/ui/Panel";
import { NETWORK_SERVICES, type NetworkService, serviceLabel } from "../../lib/networkServices";

type NetworkKey =
  | "network_ports"
  | "network_port_services"
  | "network_gap_minutes"
  | "network_excluded_sources";
type Changes = Pick<SettingsChanges, NetworkKey>;

/** The API's limits. */
const MAX_GAP_MINUTES = 60;
const MAX_PORT = 65535;

const inputClass =
  "h-10 rounded-input border border-line bg-card px-3 text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand";

const legendClass = "px-1 text-[12.5px] font-medium uppercase tracking-[0.08em] text-muted";

/** Why a gap can't be saved, or null if it can. */
function gapProblem(text: string): string | null {
  const value = text.trim();
  if (value === "") {
    return "enter a number of minutes.";
  }
  if (!/^-?\d+$/.test(value)) {
    return "use whole minutes.";
  }
  const minutes = Number(value);
  if (minutes < 1 || minutes > MAX_GAP_MINUTES) {
    return "it can be 1 to 60 minutes.";
  }
  return null;
}

function Status({ status }: { status: string | undefined }) {
  return (
    <p role="status" className="text-sm font-medium text-status-logged-fg empty:hidden">
      {status}
    </p>
  );
}

function NotSaved({ id, reason }: { id: string; reason: string | null | undefined }) {
  return reason ? (
    <p
      id={id}
      role="alert"
      className="rounded-row bg-status-needs-client-bg px-3 py-1 text-sm text-status-needs-client-fg"
    >
      Not saved: {reason}
    </p>
  ) : null;
}

function GapField({
  shown,
  status,
  error,
  onSave,
}: {
  /** What's being saved, while it saves; then what's saved. */
  shown: number;
  status: string | undefined;
  /** Why the server refused the last change. */
  error: string | undefined;
  onSave: (minutes: number) => void;
}) {
  const id = useId();
  // What's typed and not yet saved, if anything.
  const [draft, setDraft] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const reason = problem ?? error;

  function commit() {
    if (draft === null) {
      return;
    }
    const found = gapProblem(draft);
    if (found !== null) {
      setProblem(found);
      return;
    }
    const minutes = Number(draft.trim());
    setDraft(null);
    if (minutes !== shown) {
      onSave(minutes);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-medium">
        Gap between connections
      </label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_GAP_MINUTES}
          step={1}
          className={`${inputClass} w-28`}
          value={draft ?? String(shown)}
          aria-invalid={Boolean(reason)}
          aria-describedby={`${id}-unit ${id}-hint` + (reason ? ` ${id}-problem` : "")}
          onChange={(event) => {
            setDraft(event.target.value);
            setProblem(null);
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              commit();
            } else if (event.key === "Escape") {
              setDraft(null);
              setProblem(null);
            }
          }}
        />
        <span id={`${id}-unit`} className="text-muted">
          minutes
        </span>
      </div>
      <p id={`${id}-hint`} className="text-sm text-muted">
        A tech&apos;s connections to one client for one service are one session, and one activity, while no gap
        between them is longer than this. It counts from the first connection&apos;s start to the last one&apos;s
        end. 5 to start.
      </p>
      <Status status={status} />
      <NotSaved id={`${id}-problem`} reason={reason} />
    </div>
  );
}

interface ListField {
  label: string;
  hint: string;
  empty: string;
  placeholder: string;
  /** The value to add, as it's saved, or why it can't be. */
  parse: (text: string) => { value: string } | { problem: string };
}

const PORTS: ListField = {
  label: "Management ports",
  hint: "A connection counts only to one of these ports, and only to a client's address: SSH, firewall and iDRAC web admin, RDP and iDRAC's virtual console to start. Each port's service names its activity, one for each service: choose SSH for SSH on a port of its own.",
  empty: "None: no connection counts.",
  placeholder: "443",
  parse: (text) => {
    const value = text.trim();
    const port = Number(value);
    if (!/^\d+$/.test(value) || port < 1 || port > MAX_PORT) {
      return { problem: "a port is a whole number from 1 to 65,535." };
    }
    return { value: String(port) };
  },
};

const SOURCES: ListField = {
  label: "Excluded sources",
  hint: "Connections from these addresses or ranges count for no one. The Pritunl server's address goes here: its VPN traffic reaches the SonicWall as the server's, whoever it's from.",
  empty: "None: every tech's address counts.",
  placeholder: "192.168.1.10 or 192.168.1.0/24",
  parse: (text) => {
    const value = text.trim();
    return value === "" ? { problem: "enter an address, or a range." } : { value };
  },
};

/** A port's service, chosen from all six: SSH and RDP too, for a port of their own (ADR 0165). */
function ServiceSelect({
  port,
  service,
  onChoose,
}: {
  port: string;
  /** What's being saved, while it saves; then what's saved. None yet: a port just added. */
  service: NetworkService | undefined;
  onChoose: (service: NetworkService) => void;
}) {
  const id = useId();
  return (
    <>
      <label htmlFor={id} className="sr-only">
        {`Service for port ${port}`}
      </label>
      <select
        id={id}
        className={
          "h-8 min-w-0 shrink cursor-pointer rounded-input border border-line bg-card px-2 text-sm text-text " +
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        }
        value={service ?? ""}
        disabled={service === undefined}
        onChange={(event) => {
          onChoose(event.target.value as NetworkService);
        }}
      >
        {service === undefined ? <option value="">…</option> : null}
        {NETWORK_SERVICES.map((choice) => (
          <option key={choice} value={choice}>
            {serviceLabel(choice)}
          </option>
        ))}
      </select>
    </>
  );
}

function List({
  field,
  items,
  status,
  error,
  onSave,
  control,
}: {
  field: ListField;
  /** What's being saved, while it saves; then what's saved. */
  items: readonly string[];
  status: string | undefined;
  /** Why the server refused the last change. */
  error: string | undefined;
  onSave: (items: string[]) => void;
  /** Something of each item's to change, beside it: a port's service. */
  control?: (item: string) => ReactNode;
}) {
  const id = useId();
  const [text, setText] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const reason = problem ?? error;

  function add() {
    const parsed = field.parse(text);
    if ("problem" in parsed) {
      setProblem(parsed.problem);
      return;
    }
    if (items.includes(parsed.value)) {
      setProblem(`${parsed.value} is already here.`);
      return;
    }
    setText("");
    setProblem(null);
    onSave([...items, parsed.value]);
  }

  return (
    <div role="group" aria-labelledby={`${id}-label`} className="flex flex-col gap-2">
      <span id={`${id}-label`} className="font-medium">
        {field.label}
      </span>
      <p className="text-sm text-muted">{field.hint}</p>
      {items.length === 0 ? (
        <p className="text-sm text-muted">{field.empty}</p>
      ) : (
        <ul aria-label={field.label} className="flex flex-col gap-1.5">
          {items.map((item) => (
            <li
              key={item}
              className="flex items-center gap-2 rounded-row border border-line bg-card px-3 py-1.5 text-sm"
            >
              {/* A port beside its control never wraps, and the controls line up:
                  the control gives way. */}
              <span className={(control ? "w-[5ch] shrink-0" : "min-w-0 break-all") + " font-mono"}>{item}</span>
              {control ? <span className="ml-auto flex min-w-0 items-center">{control(item)}</span> : null}
              <button
                type="button"
                aria-label={`Remove ${item}`}
                className={(control ? "" : "ml-auto ") + "cursor-pointer rounded-pill px-2 text-muted hover:bg-brand-tint"}
                onClick={() => {
                  setProblem(null);
                  onSave(items.filter((i) => i !== item));
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          add();
        }}
      >
        <label htmlFor={`${id}-add`} className="sr-only">
          {`Add to ${field.label}`}
        </label>
        <input
          id={`${id}-add`}
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder={field.placeholder}
          className={`${inputClass} min-w-0 grow basis-40`}
          value={text}
          aria-invalid={Boolean(reason)}
          aria-describedby={reason ? `${id}-problem` : undefined}
          onChange={(event) => {
            setText(event.target.value);
            setProblem(null);
          }}
        />
        <button
          type="submit"
          className="h-10 cursor-pointer rounded-pill border border-line bg-card px-4 text-sm font-medium hover:bg-brand-tint"
        >
          Add
        </button>
      </form>
      <Status status={status} />
      <NotSaved id={`${id}-problem`} reason={reason} />
    </div>
  );
}

type Errors = Partial<Record<NetworkKey, string>>;

function NetworkForm({ settings }: { settings: AppSettings }) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<Errors>({});
  // The field "Saved." shows under: the last one changed, once confirmed.
  const [lastSaved, setLastSaved] = useState<NetworkKey | null>(null);
  const lastTouched = useRef<NetworkKey | null>(null);
  const save = useMutation({
    mutationFn: (changes: Changes) => api.updateSettings(changes),
    onSuccess: (result, changes) => {
      queryClient.setQueryData(["settings"], result);
      const keys = Object.keys(changes) as NetworkKey[];
      setErrors((old) => Object.fromEntries(Object.entries(old).filter(([k]) => !(k in changes))));
      const touched = lastTouched.current;
      setLastSaved(touched !== null && touched in changes ? touched : (keys[0] ?? null));
    },
    onError: (error, changes) => {
      const keys = Object.keys(changes) as NetworkKey[];
      const named = error instanceof ApiError ? error.fieldErrors.filter((e) => e.field in changes) : [];
      const failure = error instanceof Error ? error.message : "Something went wrong.";
      // Each refused field says why; a refusal naming none (or no answer)
      // shows under every field that was sent.
      const reasons: Errors = Object.fromEntries(
        named.length > 0 ? named.map((e) => [e.field, e.message]) : keys.map((k) => [k, failure]),
      );
      setErrors((old) => ({ ...old, ...reasons }));
      // A refusal may come from a change made elsewhere: show what's saved.
      if (error instanceof ApiError && error.status < 500) {
        return queryClient.invalidateQueries({ queryKey: ["settings"] });
      }
      return undefined;
    },
  });
  // One save at a time, for the whole section: changes made meanwhile combine.
  const { send, saving } = useOneSaveAtATime(save, (queued, value) => ({ ...queued, ...value }));

  // What's saved, from the cache: a save's answer is there at once.
  const saved = queryClient.getQueryData<AppSettings>(["settings"]) ?? settings;

  function shown<K extends NetworkKey>(key: K): AppSettings[K] {
    return saving?.value[key] ?? saved[key];
  }

  function change(changes: Changes) {
    const keys = Object.keys(changes) as NetworkKey[];
    lastTouched.current = keys[0] ?? null;
    setLastSaved(null);
    setErrors((old) => Object.fromEntries(Object.entries(old).filter(([k]) => !(k in changes))));
    send(changes);
  }

  function statusOf(key: NetworkKey): string | undefined {
    if (saving !== null && key in saving.value) {
      return "Saving…";
    }
    return key === lastSaved ? "Saved." : undefined;
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-muted">
        Which of the office SonicWall&apos;s connections count toward a tech&apos;s time: only from a tech&apos;s
        office address, to a client&apos;s address, on one of these ports. Each change saves as soon as you make it.
      </p>
      {/* min-w-0: a fieldset is as wide as its widest content otherwise, and
          a port's service drop-down would push it past a phone's width. */}
      <fieldset className="min-w-0 rounded-card border border-line bg-panel-2 p-4">
        <legend className={legendClass}>Connections</legend>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <List
            field={PORTS}
            items={shown("network_ports").map(String)}
            status={statusOf("network_ports") ?? statusOf("network_port_services")}
            error={errors.network_ports ?? errors.network_port_services}
            onSave={(ports) => {
              // The API keeps each port's service in step: a new one's default.
              change({ network_ports: ports.map(Number).sort((a, b) => a - b) });
            }}
            control={(port) => (
              <ServiceSelect
                port={port}
                service={shown("network_port_services")[port]}
                onChoose={(service) => {
                  // The whole map: a setting is saved whole.
                  change({ network_port_services: { ...shown("network_port_services"), [port]: service } });
                }}
              />
            )}
          />
          <GapField
            shown={shown("network_gap_minutes")}
            status={statusOf("network_gap_minutes")}
            error={errors.network_gap_minutes}
            onSave={(minutes) => {
              change({ network_gap_minutes: minutes });
            }}
          />
        </div>
      </fieldset>
      <fieldset className="rounded-card border border-line bg-panel-2 p-4">
        <legend className={legendClass}>Who&apos;s behind a connection</legend>
        <List
          field={SOURCES}
          items={shown("network_excluded_sources")}
          status={statusOf("network_excluded_sources")}
          error={errors.network_excluded_sources}
          onSave={(sources) => {
            change({ network_excluded_sources: sources });
          }}
        />
      </fieldset>
    </div>
  );
}

export function NetworkSettings() {
  const { api } = useSession();
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.getSettings(),
    retry: retryUnlessRefused,
  });

  let body;
  // A failed reload keeps the form it already has.
  if (settings.error && !settings.data) {
    body = (
      <p role="alert" className="text-muted">
        The settings could not be loaded. Reload the page to try again.
      </p>
    );
  } else if (!settings.data) {
    body = (
      <p role="status" className="text-muted">
        Loading…
      </p>
    );
  } else {
    body = <NetworkForm settings={settings.data} />;
  }

  return (
    <Panel aria-labelledby="network-heading">
      <h2 id="network-heading" className="text-lg font-semibold tracking-tight">
        Network access
      </h2>
      <div className="mt-3">{body}</div>
    </Panel>
  );
}
